import { matchPath } from "react-router-dom";

import { parseCasePath } from "../../features/caseIde/routes";
import type { HeaderCrumb } from "./HeaderBreadcrumb";

type RouteTitle = {
  /** react-router pattern; `end: true` unless it contains a splat. */
  pattern: string;
  crumbs: Array<{ label: string; to?: string }>;
};

/**
 * Default header crumbs by route, used when a page does not register its own.
 * Ordered most-specific first; the first match wins.
 */
const ROUTE_TITLES: RouteTitle[] = [
  { pattern: "/home", crumbs: [{ label: "Home" }] },
  { pattern: "/onboarding", crumbs: [{ label: "Get Started" }] },
  { pattern: "/calendar", crumbs: [{ label: "Calendar" }] },
  { pattern: "/time/review", crumbs: [{ label: "Billing Review" }] },
  { pattern: "/time", crumbs: [{ label: "Time" }] },
  {
    pattern: "/cases/new",
    crumbs: [{ label: "Cases", to: "/cases" }, { label: "New Case" }]
  },
  { pattern: "/cases", crumbs: [{ label: "Cases" }] },
  {
    pattern: "/clients/new",
    crumbs: [{ label: "Clients", to: "/clients" }, { label: "New Client" }]
  },
  {
    pattern: "/clients/:clientId",
    crumbs: [{ label: "Clients", to: "/clients" }, { label: "Client" }]
  },
  { pattern: "/clients", crumbs: [{ label: "Clients" }] },
  {
    pattern: "/companies/review",
    crumbs: [
      { label: "Companies", to: "/companies" },
      { label: "Review Employers" }
    ]
  },
  {
    pattern: "/companies/:companyId",
    crumbs: [{ label: "Companies", to: "/companies" }, { label: "Company" }]
  },
  { pattern: "/companies", crumbs: [{ label: "Companies" }] },
  { pattern: "/client", crumbs: [{ label: "Client Portal" }] },
  { pattern: "/athena/*", crumbs: [{ label: "Athena" }] },
  { pattern: "/firm-knowledge/*", crumbs: [{ label: "Firm Knowledge" }] },
  { pattern: "/firm-knowledge", crumbs: [{ label: "Firm Knowledge" }] },
  { pattern: "/automations/*", crumbs: [{ label: "Automations" }] },
  { pattern: "/automations", crumbs: [{ label: "Automations" }] },
  { pattern: "/tutorials/*", crumbs: [{ label: "Tutorials" }] },
  { pattern: "/tutorials", crumbs: [{ label: "Tutorials" }] },
  { pattern: "/pricing", crumbs: [{ label: "Pricing" }] },
  { pattern: "/design-system", crumbs: [{ label: "Design System" }] },
  { pattern: "/individual/lawfirm", crumbs: [{ label: "Law Firm Settings" }] },
  { pattern: "/individual/:id/account/*", crumbs: [{ label: "Account" }] },
  { pattern: "/law-firm/sso-setup", crumbs: [{ label: "SSO Setup" }] },
  {
    pattern: "/law-firm/sso-management",
    crumbs: [{ label: "SSO Management" }]
  },
  {
    pattern: "/admin/users/:uid/edit",
    crumbs: [{ label: "Users", to: "/admin/users" }, { label: "Update User" }]
  },
  { pattern: "/admin/users", crumbs: [{ label: "Users" }] },
  { pattern: "/admin/updates", crumbs: [{ label: "Announcements" }] },
  { pattern: "/admin/customers", crumbs: [{ label: "Customers" }] },
  { pattern: "/admin/visa-settings", crumbs: [{ label: "Visa Settings" }] },
  {
    pattern: "/admin/default-templates/new",
    crumbs: [
      { label: "Default Templates", to: "/admin/default-templates" },
      { label: "New Template" }
    ]
  },
  {
    pattern: "/admin/default-templates/:templateId",
    crumbs: [
      { label: "Default Templates", to: "/admin/default-templates" },
      { label: "Edit Template" }
    ]
  },
  {
    pattern: "/admin/default-templates/*",
    crumbs: [{ label: "Default Templates" }]
  },
  { pattern: "/admin/support-scripts", crumbs: [{ label: "Support Scripts" }] },
  {
    pattern: "/admin/documents-checklist",
    crumbs: [{ label: "Documents Checklist" }]
  },
  { pattern: "/admin/monitors", crumbs: [{ label: "Monitors" }] },
  {
    pattern: "/admin/visa-questionnaire-builder/:visa",
    crumbs: [{ label: "Questionnaire Builder" }]
  },
  {
    pattern: "/admin/exhibit-list-formats",
    crumbs: [{ label: "Exhibit List Formats" }]
  },
  {
    pattern: "/admin/law-firms/new",
    crumbs: [
      { label: "Law Firms", to: "/admin/law-firms" },
      { label: "New Law Firm" }
    ]
  },
  {
    pattern: "/admin/law-firms/:lawFirmId",
    crumbs: [
      { label: "Law Firms", to: "/admin/law-firms" },
      { label: "Law Firm" }
    ]
  },
  { pattern: "/admin/law-firms", crumbs: [{ label: "Law Firms" }] }
];

/** Default breadcrumb for a pathname, or `[]` when the route has no title. */
export function defaultCrumbsForPath(pathname: string): HeaderCrumb[] {
  for (const { pattern, crumbs } of ROUTE_TITLES) {
    const end = !pattern.endsWith("/*");
    if (matchPath({ path: pattern, end }, pathname)) {
      return crumbs.map((c, i) => ({
        id: `route-${i}`,
        label: c.label,
        to: c.to,
        current: i === crumbs.length - 1
      }));
    }
  }
  return [];
}

/**
 * Case routes own their header (`CaseHeader`), so the shared app header is
 * hidden there. Covers `/clients/:fnId/cases/:id/*`, `/cases/:id/*` and the
 * retired `/individual/:id/:visaType/*` while it redirects; account and
 * law-firm settings under `/individual/` are not case routes.
 */
export function isCaseHeaderPath(pathname: string): boolean {
  return parseCasePath(pathname) !== null;
}

/**
 * The client profile (`/clients/:clientId`) owns its header (`ClientHeader`),
 * like the case IDE. The clients list, `/clients/new` and case routes under a
 * client are not client-header routes.
 */
export function isClientHeaderPath(pathname: string): boolean {
  const match = matchPath({ path: "/clients/:clientId", end: true }, pathname);
  if (!match) return false;
  const id = (match.params.clientId ?? "").trim();
  return Boolean(id) && id !== "new";
}
